/**
 * Inline "Your extras" panel for the Customer Dashboard booking card (Iter 67q).
 *
 * Surfaces the renter's chosen insurance plan + add-ons inline so they don't
 * need to open the agreement PDF to see what they picked. Uses the same
 * colour-routed palette (`addOnPalette` for extras, emerald shield for the
 * insurance plan) as the car-detail page → the whole pre-/post-booking flow
 * now reads as one consistent visual system.
 *
 * Renders nothing when the booking has no insurance plan AND no add-ons
 * (Minimum-coverage-only bookings stay clean).
 */
import Money from "@/components/Money";
import { ShieldCheck, Receipt } from "lucide-react";
import { addOnPalette } from "@/components/car/addOnPalette";

export default function BookingExtrasPanel({ booking }) {
  const car = booking?.car || {};
  const planId = booking?.insurance_plan_id;
  const plan = planId
    ? (car.insurance_plans || []).find((p) => p.id === planId)
    : null;

  // Resolve add-on selection → full add-on docs (so we get the name + price).
  // Filtering by `find()` is intentional: when a vendor later deletes an add-on,
  // we silently drop it from the display instead of rendering a broken row.
  // Prefer the booking's priced snapshot (has qty + billed fee — survives
  // vendor catalog changes); fall back to resolving raw ids for old bookings.
  const snapshot = booking?.add_ons || [];
  const ids = booking?.add_on_ids || [];
  const addOns = snapshot.length
    ? snapshot.map((s) => ({ ...((car.add_ons || []).find((a) => a.id === s.id) || {}), ...s }))
    : ids.map((id) => (car.add_ons || []).find((a) => a.id === id)).filter(Boolean);
  const customCharges = (booking?.custom_charges || []).filter((c) => Number(c.amount));

  if (!plan && addOns.length === 0 && customCharges.length === 0) return null;

  return (
    <div className="mt-3 rounded-2xl border border-indigo-100 bg-gradient-to-br from-sky-50/40 via-white to-violet-50/40 p-3.5" data-testid={`booking-extras-${booking.id}`}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-2.5">
        Your extras
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {plan && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-2.5 flex items-center gap-2.5" data-testid={`booking-extra-plan-${booking.id}`}>
            <div className="h-8 w-8 shrink-0 rounded-lg bg-emerald-100 ring-1 ring-emerald-200 flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-emerald-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs text-emerald-900 font-semibold truncate">{plan.name}</div>
              <div className="text-[11px] text-neutral-600 truncate">
                <Money amount={plan.price_per_day} />/day · insurance
              </div>
            </div>
          </div>
        )}
        {addOns.map((a) => {
          const pal = addOnPalette(a);
          return (
            <div
              key={a.id}
              className={`rounded-xl border ${pal.ring} ${pal.bg}/60 p-2.5 flex items-center gap-2.5`}
              data-testid={`booking-extra-${booking.id}-${a.id}`}
            >
              <div className={`h-8 w-8 shrink-0 rounded-lg ${pal.medallion} ring-1 ${pal.ring} flex items-center justify-center`}>
                <pal.icon className={`h-4 w-4 ${pal.medallionFg}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-xs font-semibold truncate ${pal.fg}`}>{a.name}{(a.qty || 1) > 1 ? ` ×${a.qty}` : ""}</div>
                <div className="text-[11px] text-neutral-600 truncate">
                  {a.fee != null ? <Money amount={a.fee} /> : <><Money amount={a.price} />{a.pricing === "one_time" ? " · one-time" : "/day"}</>}
                </div>
              </div>
            </div>
          );
        })}
        {customCharges.map((c, i) => (
          <div key={`cc-${i}`} className="rounded-xl border border-slate-200 bg-slate-50/60 p-2.5 flex items-center gap-2.5" data-testid={`booking-custom-charge-${booking.id}-${i}`}>
            <div className="h-8 w-8 shrink-0 rounded-lg bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center">
              <Receipt className="h-4 w-4 text-slate-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold truncate text-slate-700">{c.label}</div>
              <div className="text-[11px] text-neutral-600 truncate"><Money amount={c.amount} /> · charge</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
